import { eCommerceGTMPurchase } from '~shared/utils/google-tag-manager';
import { decimalPriceOdds } from '~shared/utils/selections';
import {
    calculateAmericanPrice,
    getPriceOdds,
} from '~shared/components/sport-event/utils';

import { FRACTION_ODDS_FORMAT, DECIMAL_ODDS_FORMAT } from '~shared/utils/constants';
import {
    BET_BUILDER_I18N_KEY,
    BET_BUILDER_PLUS_I18N_KEY,
} from '~Betslip/uiModes/shared/GTPBetBuilder/constants';
import {
    CAST_MARKETS,
    SINGLE_RECEIPT_BET_TYPES,
} from './constants';

export const isCastMarket = ({ bet_type }) => {
    const betType = bet_type.toLowerCase();

    return CAST_MARKETS.includes(betType);
};

export const isMultiple = ({ bet_type }) => {
    const betType = bet_type.toLowerCase();

    return !(SINGLE_RECEIPT_BET_TYPES.includes(betType));
};

/**
 * Is bet a combination bet?
 * If/when we support more combination types we will need to get the type from the BE reponse
 * @param {Object} options - The bet object
 * @param {Array} [options.combination_selections=[]] - The combination selections
 * @returns {boolean} True or false
 */
export const isCombinationBet = ({
    combination_selections = {},
}) => Object.keys(combination_selections).length > 0;

export const getCombinationTypeI18nKey = (bet) => {
    const { combination_selections, selection_ids } = bet;
    const combinationsCount = Object.keys(combination_selections).length;
    if (combinationsCount === 1 && combinationsCount === selection_ids.length) {

        return BET_BUILDER_I18N_KEY;
    }

    return BET_BUILDER_PLUS_I18N_KEY;
};

export const filterPrices = ({ selection_ids = [], prices = {} }) => Object.keys(prices).reduce(
    (acc, selectionId) => {
        if (selection_ids.includes(Number(selectionId))) {
            acc[selectionId] = prices[selectionId];
        }

        return acc;
    },
    {},
);

export const normaliseCombinationBetSelections = (bet) => {
    const { selections } = bet;

    const combinationSelectionIds = Object.keys(bet.combination_selections);
    // the combination selection ids and their underlying selection ids
    const combinationRelatedSelectionIds = combinationSelectionIds.concat(
        Object.values(bet.combination_selections).flatMap(selections => selections),
    );

    // grab our combinations
    const eventAndSelectionsData = [];
    combinationSelectionIds.forEach(
        (combinationSelectionId) => {
            const selectionIds = bet.combination_selections[combinationSelectionId];
            let eventName = '';
            const eventSelections = [];
            selectionIds.forEach((selectionId, index) => {
                const selection = selections.find(({ id }) => selectionId === id);
                if (selection) {
                    if (index === 0) {
                        eventName = selection.event_name;
                    }
                    eventSelections.push({
                        name: selection.selection_name,
                        marketName: selection.market_name,
                    });
                }
            });

            eventAndSelectionsData.push({
                eventName,
                eventSelections,
            });
        }
    );
    // any others?
    selections.forEach(
        selection => {
            if (!combinationRelatedSelectionIds.includes(selection.id)) {
                eventAndSelectionsData.push({
                    eventName: selection.event_name,
                    eventSelections: [{
                        name: selection.selection_name,
                        marketName: selection.market_name,
                    }],
                });
            }
        }
    );

    return eventAndSelectionsData;
};

export const callECommerceGTMPurchase = (bet) => {
    const {
        decimal_price,
        selections, // not populated for a single
        selection_ids, // multiple and combination
        selectionId,
        prices: betPrices,
    } = bet;

    const castmarketId = isCastMarket(bet) ? selectionId : '';
    let prices = {};
    let selectionDetails = {};

    if (isCombinationBet(bet)) {
        selection_ids.forEach(selectionId => {
            selectionDetails[selectionId] = { ...bet };
            const { decimal_price: decimal } = betPrices[selectionId];
            prices[selectionId] = { decimal };
        });
    } else if (isMultiple(bet)) {
        selections.forEach(selection => {
            prices[selection.id] = { decimal: selection.decimal_price };
            selectionDetails[selection.id] = { ...selection };
        });
    } else {
        prices[selectionId] = { decimal: decimal_price };
        selectionDetails[selectionId] = { ...bet };
    }

    eCommerceGTMPurchase(bet, prices, selectionDetails, castmarketId);
};

export const flattenCombinationPrices = (selectionPrices, oddsFormat) => {
    const prices = Object.values(selectionPrices);
    if (oddsFormat === FRACTION_ODDS_FORMAT && prices.length === 1) {
        const [{ fraction_price }] = prices;
        if (fraction_price) {

            return fraction_price;
        }
    }

    const price = prices.reduce(
        (acc, { decimal_price }) => acc * decimal_price,
        1,
    );

    if (!price || !Number(price)) {

        return null;
    }

    switch (oddsFormat) {
        case DECIMAL_ODDS_FORMAT:

            return decimalPriceOdds(price);
        case FRACTION_ODDS_FORMAT:

            return getPriceOdds(price, oddsFormat);
        default:
            break;
    }

    return calculateAmericanPrice(price);
};
